import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
    Users, Copy, Check, Coins, Gift, Lightning, ShareNetwork
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function Referrals() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [applyCode, setApplyCode] = useState('');
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        fetch(`${API}/referral/stats`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : null)
            .then(setStats)
            .finally(() => setLoading(false));
    }, []);

    const copyCode = async () => {
        if (!stats?.code) return;
        const url = `${window.location.origin}?ref=${stats.code}`;
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopied(false), 2000);
    };

    const applyReferral = async () => {
        if (!applyCode.trim()) return;
        setApplying(true);
        try {
            const res = await fetch(`${API}/referral/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ code: applyCode.trim() }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
                setApplyCode('');
                // Refresh stats
                const r = await fetch(`${API}/referral/stats`, { credentials: 'include', headers: getH() });
                if (r.ok) setStats(await r.json());
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed');
            }
        } catch { toast.error('Network error'); }
        finally { setApplying(false); }
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-hot-pink text-glow-pink">INVITE</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="referrals-title">
                        Refer & Earn
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">Invite friends to Boltorium and you both earn Boltz</p>
                </motion.div>

                {/* Referral Code Card */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Card className="glass-card border-hot-pink/20 mb-6">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-xl bg-hot-pink/10 flex items-center justify-center">
                                    <ShareNetwork size={24} className="text-hot-pink" />
                                </div>
                                <div>
                                    <h3 className="font-display font-bold text-foreground">Your Referral Code</h3>
                                    <p className="text-xs text-muted-foreground">Share this with friends to earn rewards</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 font-mono text-lg text-center tracking-widest text-foreground"
                                    data-testid="referral-code">
                                    {stats?.code || '...'}
                                </div>
                                <Button onClick={copyCode} variant="outline" className="shrink-0" data-testid="copy-referral-btn">
                                    {copied ? <Check size={18} className="text-neon-green" /> : <Copy size={18} />}
                                </Button>
                            </div>

                            <Button onClick={copyCode} className="w-full bg-hot-pink text-white font-display hover:bg-hot-pink/80" data-testid="share-referral-btn">
                                <ShareNetwork size={18} className="mr-2" />Share Referral Link
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Stats */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        <Card className="glass-card">
                            <CardContent className="p-4 text-center">
                                <Users size={20} className="text-primary mx-auto mb-1" />
                                <p className="text-xl font-black font-mono text-foreground">{stats?.uses || 0}</p>
                                <p className="text-[10px] text-muted-foreground font-display">REFERRALS</p>
                            </CardContent>
                        </Card>
                        <Card className="glass-card">
                            <CardContent className="p-4 text-center">
                                <Coins size={20} className="text-neon-gold mx-auto mb-1" />
                                <p className="text-xl font-black font-mono text-neon-gold">{stats?.total_earned || 0}</p>
                                <p className="text-[10px] text-muted-foreground font-display">EARNED</p>
                            </CardContent>
                        </Card>
                        <Card className="glass-card">
                            <CardContent className="p-4 text-center">
                                <Gift size={20} className="text-hot-pink mx-auto mb-1" />
                                <p className="text-xl font-black font-mono text-hot-pink">{stats?.bonus_per_referral || 50}</p>
                                <p className="text-[10px] text-muted-foreground font-display">PER INVITE</p>
                            </CardContent>
                        </Card>
                    </div>
                </motion.div>

                {/* How it works */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <Card className="glass-card mb-6">
                        <CardContent className="p-4">
                            <h3 className="font-display text-sm text-muted-foreground tracking-wider mb-3">HOW IT WORKS</h3>
                            <div className="space-y-3">
                                {[
                                    { step: '1', text: 'Share your unique referral code or link with friends' },
                                    { step: '2', text: 'They sign up and enter your code on this page' },
                                    { step: '3', text: `You earn ${stats?.bonus_per_referral || 50} Boltz, they earn ${stats?.bonus_for_new_user || 25} Boltz` },
                                ].map(s => (
                                    <div key={s.step} className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                            <span className="text-xs font-bold text-primary font-mono">{s.step}</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">{s.text}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Apply a code */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                    <Card className="glass-card mb-6">
                        <CardContent className="p-4">
                            <h3 className="font-display text-sm text-muted-foreground tracking-wider mb-3">HAVE A REFERRAL CODE?</h3>
                            <div className="flex gap-2">
                                <Input
                                    value={applyCode}
                                    onChange={e => setApplyCode(e.target.value.toUpperCase())}
                                    placeholder="Enter code"
                                    className="font-mono uppercase tracking-widest"
                                    maxLength={12}
                                    data-testid="apply-code-input"
                                />
                                <Button onClick={applyReferral} disabled={applying || !applyCode.trim()}
                                    className="btn-primary shrink-0" data-testid="apply-code-btn">
                                    {applying ? '...' : 'Apply'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Referral history */}
                {stats?.referrals?.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                        <h3 className="font-display text-sm text-muted-foreground mb-2">YOUR REFERRALS</h3>
                        <div className="space-y-2">
                            {stats.referrals.map(r => (
                                <Card key={r.referral_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Avatar className="h-7 w-7 border border-border">
                                                <AvatarFallback className="text-[10px] bg-muted">{(r.referee_name || '?')[0]}</AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="text-sm font-medium text-foreground">{r.referee_name}</p>
                                                <p className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <Badge className="bg-neon-gold/10 text-neon-gold border-neon-gold/20 font-mono text-xs">
                                            +{r.referrer_bonus} Boltz
                                        </Badge>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
